// Featured projects, curated and verified.
// Selection criteria (in priority order):
//   1. Demonstrable  - has a working live link or a public, documented repo a reviewer can click.
//   2. On-thesis     - reinforces the AI / governance / engineering story, not a random experiment.
//   3. Finished      - real README and code, so clicking in over-delivers instead of disappointing.
//   4. Distinctive   - has a hook or story worth remembering.
// Explicitly NOT selected by commit count or star count. No invented metrics.
// `liveLink` = a verified working deployment. `link` = a public repo. Private-repo
// projects (Constitution India, Interview Tutorials) carry the card on the live demo alone.
//
// Ordering:
//   - `featured: true` marks the short list shown by default on /projects. Keep it to
//     about six. Featured cards sit at the top of this array, strongest first; the
//     homepage shows the first two.
//   - The "All" view groups cards into sections by their FIRST category, in
//     PROJECT_CATEGORIES order. Within a section, cards with a live demo come before
//     repo-only cards; otherwise array order holds.
//
// `categories` drives the filter chips on /projects. A project can belong to more
// than one (a browser-native RAG app is also an AI app). Keep values to the small
// set in PROJECT_CATEGORIES below so the chip row stays short.

export const PROJECT_CATEGORIES = [
	"Governance & Security",
	"AI Apps",
	"Browser-native",
	"Developer Tools",
	"Space",
	"Places",
];

const featuredProjects = [
	{
		title: "Guardrail Review",
		description:
			"A free tool for teams whose AI safety filter refuses legitimate work. Paste the prompts your guardrail blocked, sort each one in a few taps, and download a one-page report for leadership mapped to the NIST AI Risk Management Framework. Everything runs in your browser, with no storage and no network calls. A companion demo shows who gets blocked, from a nurse asking about insulin limits to me, locked out of my own bathroom. Its people and scores are illustrative and labelled as such.",
		enterprise:
			"Gives an AI governance team a way to measure how often its guardrails block legitimate work and report it to risk leadership in NIST AI RMF terms, without customer prompts leaving the analyst's laptop.",
		image: "/projects/guardrail-review.png",
		tags: ["AI Guardrails", "NIST AI RMF", "Browser-native"],
		categories: ["Governance & Security", "Browser-native"],
		featured: true,
		impact: "Live demo",
		liveLink: "https://guardrails.swapniltamse.com",
		liveText: "Live Demo",
		link: "https://swapniltamse.com/article/guardrail-review-bathroom-lockout",
		linkText: "Why I built it",
	},
	{
		title: "Ask the Declaration",
		description:
			"Semantic search over America's founding documents, built for the country's 250th. Ask a question in plain English and get the founders' own words back, with exact citations like Article I, Section 8. Retrieval, embeddings, and answer synthesis all run in your browser, with no servers, no API keys, and no tracking. My first production retrieval system.",
		enterprise:
			"The pattern behind a policy and procedures assistant: every answer cites the exact clause, and search runs on the device, so no document or query leaves it.",
		image: "/projects/ask-the-declaration.png",
		tags: ["RAG", "Embeddings", "Browser-native"],
		categories: ["Browser-native", "AI Apps"],
		featured: true,
		impact: "Live and open source",
		liveLink: "https://askthedeclaration.com",
		liveText: "Live Demo",
		link: "https://github.com/swapniltamse/ask-the-declaration",
		linkText: "View Code",
	},
	{
		title: "Nobel 2026, Explained",
		description:
			"The 2026 Nobel Prizes in plain words, with six reader versions per prize (age 10 to skeptic), scripts for explaining each one out loud, a quiz and a printable classroom handout, in eight languages. Built with Claude Code agents: one researches from the Nobel committees' own documents, one writes the reader versions, and an independent checker re-reads every source. It made 37 public corrections across four prizes, each logged on the page it fixed.",
		enterprise:
			"A working model of AI-generated content under review: a separate checker agent verifies every claim against source documents, and every correction is logged where it was made.",
		image: "/projects/nobel-explainers.png",
		tags: ["Claude Code", "Multi-agent", "Cloudflare"],
		categories: ["AI Apps"],
		featured: true,
		impact: "Live, 8 languages",
		liveLink: "https://nobelprize.swapniltamse.com",
		liveText: "Live Demo",
		link: "https://swapniltamse.com/article/nobel-explainer-agent-pipeline",
		linkText: "How it was built",
	},
	{
		title: "claude-security-engineer",
		description:
			"An OWASP-style security layer for AI coding agents. It intercepts the insecure patterns agents reach for by default, such as wrong parameterized queries, unallowlisted URLs, weak password hashing, and missing auth logging, before the code is ever written rather than after an incident.",
		enterprise:
			"Secure development controls for AI coding assistants, so OWASP issues are blocked when the code is generated instead of found later in review or a penetration test.",
		image: "/projects/claude-security-engineer.png",
		tags: ["AI Security", "Claude Code", "OWASP"],
		categories: ["Governance & Security", "Developer Tools"],
		featured: true,
		impact: "Open source",
		link: "https://github.com/swapniltamse/claude-security-engineer",
		linkText: "View Code",
	},
	{
		title: "Space Data Atlas",
		description:
			"Every NASA Space Apps 2026 challenge, traced to the NASA data that can solve it. Each of the 14 challenges gets suggested datasets in order of usefulness, what access each one needs, starter code that was run before publishing, and related 2025 winners. 57 datasets, including hard-to-find ones like NASA's catalogue of hardware left on the Moon, with every NASA catalog ID checked live. An MCP server lets teams query the same index from Claude Code or Cursor. Independent and free.",
		enterprise:
			"The pattern behind an internal data catalog: each dataset lists its access requirements and a tested starter query, and an MCP server lets approved AI tools query the same index.",
		image: "/projects/space-data-atlas.png",
		tags: ["MCP", "Cloudflare", "Open Data"],
		categories: ["Space", "Developer Tools"],
		featured: true,
		impact: "Live and open source",
		liveLink: "https://spacedata.swapniltamse.com",
		liveText: "Live Demo",
		link: "https://github.com/swapniltamse/space-data-atlas",
		linkText: "View Code",
	},
	{
		title: "Weighing the Moon (GRAIL)",
		description:
			"An interactive tribute to NASA's GRAIL mission. Spin the Moon's real gravity field on a WebGL globe you drag like Google Earth, click nineteen real basins and landmarks (Apollo 11's landing site and the ridge where the spacecraft were crashed among them), and recreate a MoonKAM image request. Every number and image is real NASA public-domain data, with dark and light modes and a clear independent-tribute note. Built for the mission's fifteen-year mark.",
		enterprise:
			"Turns a dense technical dataset into a view non-specialists can explore, the same problem as explaining risk or exposure data to executives.",
		image: "/projects/grail-weighing-the-moon.png",
		tags: ["three.js", "WebGL", "Data Viz"],
		categories: ["Space", "Browser-native"],
		featured: true,
		impact: "Live demo",
		liveLink: "https://grail.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "claude-staff-engineer",
		description:
			"An enforcement harness that governs what an AI agent does before it writes a line: discovery passes, blast-radius limits, migration gates, postmortem checks, and observability requirements. The judgment a staff engineer applies automatically, encoded as guardrails for the agent.",
		enterprise:
			"Change management for AI agents: blast-radius limits, migration gates and observability requirements are enforced before an agent changes production code.",
		image: "/projects/claude-staff-engineer.png",
		tags: ["AI Guardrails", "Claude Code", "Reliability"],
		categories: ["Governance & Security", "Developer Tools"],
		impact: "Open source",
		link: "https://github.com/swapniltamse/claude-staff-engineer",
		linkText: "View Code",
	},
	{
		title: "Titan Data Atlas",
		description:
			"A plain-language front door to the seven public Titan data products from the Hayes Research Group at Cornell, most of them made from Cassini's radar views of Saturn's largest moon. Each product shows what it is, its level, size, formats, DOI, credit and license, with step-by-step recipes for ArcGIS Pro, QGIS and JMARS. Converted samples open on a map drawn in Titan's own longitude and latitude, and a read-only MCP server lets researchers query the same catalogue from Claude or Cursor. Independent and free.",
		enterprise:
			"Data lineage for outside users: every product carries its DOI, license and credit, and AI tools get read-only access to the catalog.",
		image: "/projects/titan-data-atlas.png",
		tags: ["MCP", "GIS", "Open Data"],
		categories: ["Space", "Developer Tools"],
		impact: "Live demo",
		liveLink: "https://titan.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "Mars Image Atlas",
		description:
			"Type a crater, volcano or landing site and see every image THEMIS, HiRISE and CTX have taken of it, with a preview of each one, its files, and the exact steps to load it in JMARS, ASU's free desktop GIS for Mars. Search runs on NASA's Orbital Data Explorer and the IAU's official Mars place names, and a read-only MCP server answers the same questions from Claude or Cursor. Independent and free.",
		enterprise:
			"One search across several authoritative sources, exposed to AI assistants through a read-only interface, which is how I would expose a reference data service.",
		image: "/projects/mars-image-atlas.png",
		tags: ["MCP", "Planetary Data", "Open Data"],
		categories: ["Space", "Developer Tools"],
		impact: "Live demo",
		liveLink: "https://marsimages.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "MoonKAM Recovered Archive",
		description:
			"About 5,800 original images from NASA's GRAIL MoonKAM program, the Moon cameras that middle-school classrooms aimed during the 2012 mission. The public gallery went offline for over a decade. I recovered the set from a mission backup, rebuilt the index from Internet Archive captures of the retired gallery, matched every frame on request code and millisecond capture time, and released it as public-domain downloads with a per-image catalogue. Frames carrying student names were held back.",
		enterprise:
			"Records recovery with privacy controls: a lost index rebuilt from backups and archived captures, records matched on exact keys, and anything carrying personal data held back before release.",
		image: "/projects/moonkam-archive.png",
		tags: ["Open Data", "Digital Preservation"],
		categories: ["Space"],
		impact: "Public release",
		liveLink: "https://grail.swapniltamse.com/spif/",
		liveText: "Downloads",
		link: "https://github.com/swapniltamse/moonkam-archive",
		linkText: "View Catalogue",
	},
	{
		title: "Wiring the Moon",
		description:
			"An interactive explainer of what NASA says a lunar South Pole base will need from its network: bandwidth, navigation, timing and eyes in orbit, phase by phase. One phase switch drives every figure and a 3D South Pole built on LRO imagery in its native projection. Every number is tagged to one of eight NASA documents, nine places those documents disagree are logged side by side, and a monthly GitHub Action re-verifies 53 quotes against the source PDFs. Zero WCAG 2.1 AA violations.",
		enterprise:
			"Source-traceable reporting: every figure links to a document, conflicts between sources are logged instead of hidden, and a scheduled job re-checks every quote.",
		image: "/projects/wiring-the-moon.png",
		tags: ["three.js", "Data Viz", "Verification"],
		categories: ["Space", "Browser-native"],
		impact: "Live and open source",
		liveLink: "https://moonbase.swapniltamse.com",
		liveText: "Live Demo",
		link: "https://github.com/swapniltamse/moonbase-explainer",
		linkText: "View Code",
	},
	{
		title: "Karwar Atlas",
		description:
			"The story of Karwar, the town on the Kali river where my mother's family comes from, told in 122 short stories you can read in a minute and share in a tap. Every story names its source, from the 1883 Bombay Gazetteer to census tables and newspapers, and the whole site reads in English, Kannada, Konkani and Marathi. Each story has its own share link and a picture card sized for Instagram, and open-data charts show Karwar's rain, sea temperature and storms. A sensitivity review rechecked every community name and honorific in all three translations.",
		enterprise:
			"Content in four languages with a sensitivity review before release, the same step customer communications need in a multilingual market.",
		image: "/projects/karwar-atlas.png",
		tags: ["Multilingual", "Open Data", "Heritage"],
		categories: ["Places"],
		impact: "Live site",
		liveLink: "https://karwar.swapniltamse.com",
		liveText: "Visit the Atlas",
	},
	{
		title: "Umlijoog Atlas",
		description:
			"The history of Umlijoog, the hamlet on the Kali river near Karwar where my father's family comes from, written for the elders who live there. Old maps from 1781 onward, gazetteer records, census counts from 1951 to 2011, rainfall, sea temperature and satellite photos become short chapters in simple words, in English, Kannada, Konkani and Marathi. A year picker shows what was happening in any year, and a last chapter asks readers to add what their elders remember. Built for first-time phone users, with bigger text and one chapter at a time.",
		enterprise:
			"Accessibility designed in for first-time phone users, the same constraint as serving elderly or newly banked customers.",
		image: "/projects/umlijoog-atlas.png",
		tags: ["Multilingual", "Open Data", "Heritage"],
		categories: ["Places"],
		impact: "Live site",
		liveLink: "https://joog.swapniltamse.com",
		liveText: "Visit the Atlas",
	},
	{
		title: "Paris Transit Helper",
		description:
			"A real-time companion for navigating Paris public transport. Live 2026 ticket prices and fare rules, emergency help, troubleshooting, and an AI transit assistant, with multilingual support built for tourists arriving without the local knowledge.",
		enterprise:
			"Real-time customer self-service in several languages, with an AI assistant layered on top of verified rules rather than replacing them.",
		image: "/projects/paris-transit-helper.png",
		tags: ["React", "Real-time", "i18n"],
		categories: ["Places", "AI Apps"],
		impact: "Live demo",
		liveLink: "https://paristransithelper.com",
		liveText: "Live Demo",
	},
	{
		title: "Aarti",
		description:
			"A free, offline-first Ganpati aarti reader, built with Claude Code overnight, timed to Ganesh Chaturthi. Every line pairs Devanagari with a Roman spelling tuned to how Marathi is actually sung, not Hindi transliteration rules, plus a cited meaning a tap away. No line is typed from memory: a script pulls the text from its published source, and two independent sources are checked against each other for every aarti.",
		enterprise:
			"Content provenance: no line typed from memory, every line pulled from a published source and checked against a second source before release.",
		image: "/projects/aarti.png",
		tags: ["Claude Code", "PWA", "Offline-first"],
		categories: ["AI Apps"],
		impact: "Live demo",
		liveLink: "https://aarti.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "AI Is Not Magic",
		description:
			"A plain-language, interactive explainer that shows non-technical people how AI actually works and where it breaks. Nine short scenes, each a live demo: watch it guess the next word, watch it get confidently wrong, watch it forget. No math, no jargon, and nothing you type ever leaves the page.",
		enterprise:
			"AI literacy material for non-technical staff and board members that shows where models fail, the training an enterprise AI rollout needs.",
		image: "/projects/ai-is-not-magic.png",
		tags: ["AI Literacy", "Interactive", "Education"],
		categories: ["AI Apps"],
		impact: "Live demo",
		liveLink: "https://notmagic.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "Litmus Test",
		description:
			"An instant food analyzer for people managing diet and gut health. Enter a food and get its pH balance (acidic, alkaline, or neutral), its bloating potential, and an allergen alert in seconds. A clean, focused consumer tool that turns a vague health question into a quick answer.",
		enterprise:
			"Turns a vague question into one clear answer in seconds, the design goal for small customer-facing tools inside a banking app.",
		image: "/projects/litmus-test.png",
		tags: ["React", "Health", "Consumer"],
		categories: ["AI Apps"],
		impact: "Live demo",
		liveLink: "https://www.litmustest.online",
		liveText: "Live Demo",
	},
	{
		title: "Momentum",
		description:
			"A one-button task starter for people who find starting harder than doing. Say who you are right now, from banker to new mom to retired, and how much energy you have, then press one button for a single small action with a timer already attached. No signup, no lists, no streaks, no yesterday. Refusal is a first-class action, so the app hands you the next step instead of arguing with you.",
		enterprise:
			"Design for people under cognitive load, one action at a time, which applies to operations staff working an alert queue.",
		image: "/projects/momentum-2.png",
		tags: ["Next.js", "ADHD", "PWA"],
		categories: ["AI Apps"],
		impact: "Live demo",
		liveLink: "https://momentum.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "Interview Tech Tutorials",
		description:
			"Eight interactive, single-file tutorials for senior interview revision, covering React, C#/.NET, Java, AWS, System Design, and the GenAI retrieval stack. Each one teaches by building one small app and explains every concept the same way: the problem first, then an analogy, then plain words, and only then the code.",
		enterprise:
			"A repeatable teaching format for upskilling engineers on cloud, system design and GenAI.",
		image: "/projects/interview-tech-tutorials.png",
		tags: ["Teaching", "GenAI", "Interactive"],
		categories: ["AI Apps", "Developer Tools"],
		impact: "Live and self-contained",
		liveLink: "https://techtutorials.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "Family Recipe Agent",
		description:
			"Preserve a family's recipes before they are forgotten. Each family member gets her own AI agent trained on her kitchen; you describe a dish from memory and the agent reconstructs the recipe. Built with Claude Code, inspired by moms who never measured anything.",
		enterprise:
			"Knowledge capture from experts before they leave, the same problem as retiring specialists who are the only ones who understand a legacy system.",
		image: "/projects/family-recipe-agent.png",
		tags: ["AI Agents", "Claude", "React"],
		categories: ["AI Apps"],
		impact: "Live and open source",
		liveLink: "https://familyrecipe.swapniltamse.com",
		liveText: "Live Demo",
		link: "https://github.com/swapniltamse/mhardolkar-family-recipe-agent",
		linkText: "View Code",
	},
	{
		title: "Wiser Bite",
		description:
			"Same food, different wisdom. Instead of one fixed score per food, it answers wise for whom, when, and at what pace of change, using a structured hybrid RAG where the rules and safety are deterministic and only the voice is generated. Retrieval runs in your browser over a small principle corpus with Transformers.js, and your profile never leaves the device. Two sibling tabs, Move and Mind, apply the same contextual idea to a small movement or a thought loop.",
		enterprise:
			"Hybrid RAG where rules and safety checks are deterministic and only the wording is generated, the pattern I would use for customer-facing AI in a regulated firm.",
		image: "/projects/wiser-bite.png",
		tags: ["RAG", "Transformers.js", "Local-first"],
		categories: ["Browser-native", "AI Apps"],
		impact: "Live demo",
		liveLink: "https://wiserbite.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "Parenting-OS",
		description:
			"A local-first, voice-first infant tracker I built for one family: two tired parents, one baby, two phones. You say or tap what happened and it logs on-device in about thirty milliseconds, with the network as a background sync, never a requirement. It carries a CDC-based milestones guide, pediatrician-style reports, and a postpartum wellness space whose maternal mental health search runs entirely in the browser, so a mother's hardest questions never leave her phone. Claude is offered as an opt-in chat, never the default.",
		enterprise:
			"Privacy by design for sensitive data: on-device processing by default, cloud AI only when the user opts in, and the app keeps working offline.",
		image: "/projects/parenting-os-nir.png",
		tags: ["Local-first", "Voice", "On-device RAG"],
		categories: ["Browser-native", "AI Apps"],
		impact: "Live demo",
		liveLink: "https://baby.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "Ask the Constitution (India)",
		description:
			"The same browser-native retrieval architecture pointed at the Constitution of India. 602 citation-ready passages are embedded at build time and searched on-device with Transformers.js. Every answer cites the exact article in its own words and never generates a legal claim.",
		enterprise:
			"Grounded answers with no generated claims, the pattern for legal, compliance or policy search where an invented answer is a liability.",
		image: "/projects/ask-the-constitution-india.png",
		tags: ["RAG", "Transformers.js", "Legal Tech"],
		categories: ["Browser-native", "AI Apps"],
		impact: "Live demo",
		liveLink: "https://asktheindianconstitution.com",
		liveText: "Live Demo",
	},
	{
		title: "Understand Liechtenstein",
		description:
			"The same browser-native engine turned into a bilingual guide to a country most people have never read about. It leads as a travel guide (an interactive map of all 11 municipalities, an on-device day planner, itineraries) and closes with a search over the full Constitution of the Principality, cited verbatim in German or English. Everything runs on your device with open-weights models, works offline after the first load, and never generates a word of law. Launching on National Day, August 15.",
		enterprise:
			"Open-weights models running on the device, one way to use AI where data residency rules or vendor restrictions block hosted models.",
		image: "/projects/understand-liechtenstein.png",
		tags: ["On-device AI", "Open weights", "Bilingual"],
		categories: ["Browser-native", "AI Apps", "Places"],
		impact: "Live demo",
		liveLink: "https://liechtenstein.swapniltamse.com",
		liveText: "Live Demo",
	},
	{
		title: "AaplaManus",
		description:
			"Local-first multi-agent AI built on Ollama. Browser, code, research, and file agents work together on your own hardware, with no API keys and no data leaving the machine. Cloud is an opt-in escape hatch, not the default, and every session shows the cost saved versus a hosted model.",
		enterprise:
			"Multi-agent AI on your own hardware, with no data sent to a third-party API and cost tracked against hosted models.",
		image: "/projects/aaplamanus.png",
		tags: ["Multi-agent", "Local LLM", "Python"],
		categories: ["Developer Tools"],
		impact: "Open source",
		link: "https://github.com/swapniltamse/AaplaManus",
		linkText: "View Code",
	},
	{
		title: "RAG Docs Chatbot",
		description:
			"A retrieval-augmented chatbot that answers questions grounded in your own static documents. Built to experiment with chunking strategy and context switching, and documented honestly, including where retrieval succeeds and where it still misses.",
		enterprise:
			"Records where retrieval fails as well as where it works, the evaluation evidence a model validation team asks for.",
		image: "/projects/rag-docs-chatbot.png",
		tags: ["RAG", "Chunking", "Chatbot"],
		categories: ["Developer Tools"],
		impact: "Open source",
		link: "https://github.com/swapniltamse/rag-docs-chatbot",
		linkText: "View Code",
	},
	{
		title: "token-optimizer",
		description:
			"A Claude Code habit tool that catches the moment you ask an AI to do something you already know how to do, like writing a commit message or renaming a variable. It nudges you to run the command yourself, cutting token spend by fixing the habit rather than the tool.",
		enterprise:
			"AI cost control at the developer level, cutting spend by changing usage habits instead of restricting access.",
		image: "/projects/token-optimizer.png",
		tags: ["Developer Tools", "Claude Code", "Cost"],
		categories: ["Developer Tools"],
		impact: "Open source",
		link: "https://github.com/swapniltamse/token-optimizer",
		linkText: "View Code",
	},
	{
		title: "mcp-starter",
		description:
			"A batteries-included MCP server configuration for Claude Code, covering general development, AI/ML work, and team shipping workflows. A reusable template for wiring Model Context Protocol tools into an agent without starting from scratch.",
		enterprise:
			"A standard, reusable configuration for agent tools, so teams start from one governed setup instead of each building their own.",
		image: "/projects/mcp-starter.png",
		tags: ["MCP", "Agent Tooling", "Template"],
		categories: ["Developer Tools"],
		impact: "Open source",
		link: "https://github.com/swapniltamse/mcp-starter",
		linkText: "View Code",
	},
];

export default featuredProjects;
